import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const AdminResetPassword = () => {
  if (typeof document !== "undefined") document.title = "Set a new password — School Smiles Australia";
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    if (!token) return setError("Missing or invalid reset token. Please request a new link.");
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => navigate("/admin/login", { replace: true }), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F9FF] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-2xl px-5 py-3 shadow-sm border border-[#E5F0FA]">
            <Logo />
          </div>
        </div>
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(20,50,74,0.06)] border border-[#E5F0FA] p-8 md:p-10">
          <h1 className="font-display font-bold text-2xl md:text-3xl text-[#14324A]">Set a new password</h1>
          <p className="mt-2 text-sm text-[#4B6B86]">Choose a strong password of at least 8 characters.</p>

          {done ? (
            <div className="mt-8 text-center" data-testid="admin-reset-done">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#F4F9FF] text-[#1677C8] inline-flex items-center justify-center">
                <CheckCircle2 size={28} strokeWidth={2.5}/>
              </div>
              <p className="mt-4 text-sm text-[#4B6B86]">Password updated. Redirecting to sign in…</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="admin-reset-form">
              <div>
                <Label className="text-sm font-medium text-[#14324A]" htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-reset-password"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#14324A]" htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="admin-reset-confirm"
                  className="mt-2"
                />
              </div>
              {error && (
                <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="admin-reset-submit">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                {submitting ? "Updating..." : "Update password"}
              </button>
              <Link to="/admin/login" className="block text-sm text-center text-[#4B6B86] hover:text-[#14324A]">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPassword;
